import { TOUR_POSITION_STORAGE_KEY } from '../data/Config';
import * as Actions from './actions';
import AppState, { TemplateEditMode } from './AppState';
import { Action, createBrowserHistory, Location } from 'history';
import Plan from '../buy/Plan';
import { trackPageChange } from '../utils/Tracker';
import TourStep from '../tour/TourSteps';
import { Resolution } from '../data/Resolution';
import { LOG_STATUS_STORAGE_KEY } from '../data/Config';
import {
  getValueFromLocalStorage,
  isBrowserFullySupported,
  isMobileLayout
} from '../utils/index';
import Layer from '../data/Layer';
import { ModalData, ModalType } from '../data/ModalData';
import { PlatformType } from 'data/PlatformType';
import { TextFrame } from 'data/TextFrame';
import { getShapePoints, TextFrameData } from 'data/FlyerData';
import { storeDispatch } from './Store';
import { AddFigmaLayersPayload } from './actions/payloads';

const history = createBrowserHistory();
history.listen((location: Location, action: Action) => {
  trackPageChange(location.pathname);
  if (action === 'POP') {
    storeDispatch({ type: Actions.DID_GO_BACK_WITH_BROWSER });
  }
});

let tourIndex = TourStep.AddEffect;

const platform = (window as any).beatflyerPlatform as PlatformType;
console.log(`Platform: ` + platform);

let isAutoLogging = false;
const isBeatflyerLite = ![
  PlatformType.Web,
  PlatformType.Desktop,
  PlatformType.iOSApp
].includes(platform);
if (!isBeatflyerLite) {
  let storedTourIndex = getValueFromLocalStorage(TOUR_POSITION_STORAGE_KEY);
  if (storedTourIndex !== null) {
    tourIndex = parseInt(storedTourIndex, 10);
  }
  isAutoLogging = getValueFromLocalStorage(LOG_STATUS_STORAGE_KEY) === 'true';
}

const isRegistering = document.location.pathname.includes('/appsumo/');

const initialModals: ModalData[] = [];
if (
  !isBrowserFullySupported() &&
  !isBeatflyerLite &&
  !isRegistering &&
  !isMobileLayout()
) {
  initialModals.push({ type: ModalType.UnsupportedBrowser, uncloseable: true });
}

const isCloudRenderer = (window as any).isCloudRenderer as boolean;
const hideRendererWatermark =
  new URLSearchParams(document.location.search).get('hideWatermark') === 'true';

const reducer = (
  state: AppState = {
    userHasInteractedWithPage: false,
    // flowState: isFigmaPlugin ? FlowState.LoadingExample : FlowState.Editing,
    error: null,
    user: null,
    isAutoLogging,
    lastUserUpdate: 0,
    alerts: [],
    recorder: null,
    // recordingBlob: null,
    // recordingURL: null,
    // recordingWasSmooth: true,
    videoThumbnail: null,
    history,
    // hasDownloadedRecording: false,
    tourIndex,
    isRenderingPipeline: false,
    platform,
    deviceModel: (window as any).beatflyerDeviceModel,
    isBeatflyerLite,
    selectedFrame: null,
    selectedMask: null,
    editedShape: null,
    proposedEditedShape: null,
    openedModals: initialModals,
    controllerHasConfirmation: false,
    templateUI: null,
    templateOptions: null,
    templateEditMode: TemplateEditMode.None,
    isCloudRenderer,
    hideRendererWatermark,
    detachColorTheme: false,
    importDesign: false
    // hasUnrecordedChanges: false
  },
  action: any
): AppState => {
  switch (action.type) {
    case Actions.ADD_AUDIO:
      return {
        ...state,
        openedModals: []
      };

    case Actions.WILL_LOAD_EXAMPLE: {
      const modalData: ModalData = {
        type: ModalType.Processing,
        data: 'Loading',
        uncloseable: true
      };
      return {
        ...state,
        // hasMadeChanges: false,
        openedModals: [modalData]
      };
    }
    case Actions.LOAD_EXAMPLE:
      return {
        ...state,
        // hasMadeChanges: true,
        templateUI: action.templateUI || null,
        templateOptions: action.templateOptions || null,
        templateEditMode: action.templateUI
          ? TemplateEditMode.Simple
          : TemplateEditMode.None,
        openedModals: []
      };

    case Actions.START_RECORDING: {
      if (state.selectedFrame) {
        state.selectedFrame.didUnselect();
      }
      const modalData: ModalData = {
        type: ModalType.Recording,
        data: action.recorder,
        uncloseable: true
      };
      return {
        ...state,
        recorder: action.recorder,
        videoThumbnail: action.videoThumbnail,
        // hasDownloadedRecording: false,
        // recordingBlobCompressed: undefined,
        selectedFrame: null,
        selectedMask: null,
        openedModals: [modalData]
        // hasUnrecordedChanges: false
      };
    }
    case Actions.RECORDING_STOPPED:
      // if (state.recordingURL) {
      //   URL.revokeObjectURL(state.recordingURL);
      // }
      return {
        ...state,
        // recordingBlob: null,
        // recordingURL: null,
        recorder: null,
        videoThumbnail: null,
        openedModals: []
      };

    case Actions.RECORDING_ENDED:
      if (state.platform === PlatformType.iOSApp) {
        return {
          ...state,
          recorder: null,
          videoThumbnail: null,
          openedModals: []
        };
      } else {
        return state;
      }

    case Actions.RECORDING_READY: {
      const modalData: ModalData = {
        type: ModalType.ReviewRecording,
        data: {
          type: action.recordType,
          url: action.url,
          smoothness: action.smoothness
        }
      };
      return {
        ...state,
        // flowState: FlowState.Ready,
        // recordingBlob: action.blob,
        // recordingURL: action.url || URL.createObjectURL(action.blob),
        openedModals: [modalData],
        recorder: null
      };
    }

    case Actions.VIDEO_SHARED:
      return {
        ...state
        // flowState: FlowState.Shared
      };

    case Actions.SHOWN_ERROR:
      return {
        ...state,
        error: null
      };

    case Actions.USER_SIGN_IN:
      return {
        ...state,
        user: action.user,
        isAutoLogging: false,
        lastUserUpdate: Date.now()
      };

    case Actions.UPDATE_USER:
      if (state.user) {
        state.user.plan = action.data.plan || Plan.Free;
        state.user.customerId = action.data.customerId;
        state.user.planExpiration = action.data.planExpiration || 0;
        state.user.countryCode = action.data.countryCode;
        state.user.cardFingerprint = action.data.cardFingerprint;
        state.user.taxInfo = action.data.taxInfo;
        state.user.currency = action.data.currency;
        state.user.createdVideos = action.data.createdVideos || 0;
        state.user.createdGifs = action.data.createdGifs || 0;
        state.user.customResolutions = [];
        state.user.colorThemes = action.data.colorThemes ?? [];
        if (action.data.customResolutions) {
          for (let i in action.data.customResolutions) {
            const d = action.data.customResolutions[i];
            state.user.customResolutions.push(d);
          }
          state.user.customResolutions.sort((a: Resolution, b: Resolution) => {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
        }
        state.user.customFonts = action.data.customFonts;
      }
      return {
        ...state,
        lastUserUpdate: Date.now()
      };

    case Actions.UPDATE_USER_COLOR_THEMES:
      if (state.user) {
        state.user.colorThemes = action.themes;
      }
      return {
        ...state,
        lastUserUpdate: Date.now()
      };

    case Actions.BLOCK_USER:
      if (state.user) {
        state.user.isBlocked = true;
      }
      return state;

    case Actions.RESET:
      // if (state.recordingURL) {
      //   URL.revokeObjectURL(state.recordingURL);
      // }
      return {
        ...state,
        // flowState: FlowState.Editing,
        // recordingBlob: null,
        // recordingURL: null,
        // hasMadeChanges: false,
        selectedFrame: null,
        selectedMask: null,
        controllerHasConfirmation: false,
        templateUI: null,
        templateOptions: null,
        templateEditMode: TemplateEditMode.None,
        detachColorTheme: false,
        importDesign: false
        // hasUnrecordedChanges: false
      };

    case Actions.SPLIT_LAYER:
      return {
        ...state,
        openedModals: []
      };

    case Actions.SHOW_ALERT:
      return {
        ...state,
        alerts: state.alerts.concat([action.alert])
      };
    case Actions.CLOSE_ALERT:
      const alerts = state.alerts.concat([]);
      alerts.pop();
      return {
        ...state,
        alerts
      };
    case Actions.CLOSE_ALL_ALERTS:
      return {
        ...state,
        alerts: []
      };

    case Actions.CHANGE_RESOLUTION:
      return {
        ...state,
        selectedFrame: null
      };

    case Actions.REMOVE_FRAME:
      return {
        ...state,
        selectedFrame: null
      };

    case Actions.REMOVE_LAYER: {
      let { selectedFrame } = state;
      if (selectedFrame) {
        const layer = action.layer as Layer;
        if (layer.frames.indexOf(selectedFrame) > -1) {
          selectedFrame = null;
        }
      }
      return {
        ...state,
        selectedFrame
      };
    }

    case Actions.USER_DID_INTERACT:
      return {
        ...state,
        userHasInteractedWithPage: true
      };

    // case Actions.UPDATE_TOUR_STEP:
    //   return {
    //     ...state,
    //     tourIndex: action.step
    //   };

    case Actions.WILL_START_RENDERING_QUEUE:
      return {
        ...state,
        isRenderingPipeline: true
      };

    case Actions.ADDED_FIGMA_LAYERS: {
      const { showIntro } = action as AddFigmaLayersPayload;
      if (!showIntro) {
        return state;
      }
      return {
        ...state,
        openedModals: [{ type: ModalType.FigmaWelcome }, ...state.openedModals]
      };
    }

    case Actions.UPDATE_FIGMA_PAYMENT_STATUS: {
      return {
        ...state,
        figmaPaymentStatus: action.status
      };
    }

    case Actions.SELECT_FRAME: {
      return {
        ...state,
        selectedFrame: action.frame
      };
    }

    case Actions.SELECT_MASK: {
      return {
        ...state,
        selectedMask: action.layer
      };
    }

    case Actions.OPEN_MODAL: {
      const openedModals = action.stack
        ? state.openedModals.concat(action.modalData)
        : [action.modalData];
      return {
        ...state,
        openedModals
      };
    }
    case Actions.CLOSE_MODAL: {
      const openedModals = state.openedModals.slice();
      openedModals.pop();
      return {
        ...state,
        openedModals
      };
    }
    case Actions.DID_GO_BACK_WITH_BROWSER: {
      return {
        ...state,
        openedModals: []
      };
    }

    case Actions.EDIT_TIMELINE:
      return {
        ...state,
        controllerHasConfirmation: true,
        audioWaveSrc: action.src
      };
    case Actions.EDIT_MOTION_SOURCE:
    case Actions.BROWSE_EFFECTS:
      return {
        ...state,
        controllerHasConfirmation: true
      };
    case Actions.CANCEL_EDIT_MOTION_SOURCE:
    case Actions.SAVE_EDIT_MOTION_SOURCE:
    case Actions.CANCEL_EDIT_TIMELINE:
    case Actions.SAVE_EDIT_TIMELINE:
    case Actions.CANCEL_EFFECT_PREVIEW:
    // case Actions.ADD_PREVIEWED_EFFECT:
    case Actions.ADDED_FILTER:
      return {
        ...state,
        controllerHasConfirmation: false
      };

    case Actions.CHANGE_TEMPLATE_EDIT_MODE:
      return {
        ...state,
        templateEditMode: action.mode,
        selectedFrame: null
      };

    case Actions.EDITED_TEXT: {
      let lastEditedTextStyle = state.lastEditedTextStyle;
      if (action.frame && action.frame instanceof TextFrame) {
        lastEditedTextStyle = (action.frame as TextFrame).asData(
          true
        ) as TextFrameData;
        lastEditedTextStyle = JSON.parse(JSON.stringify(lastEditedTextStyle));
      }
      return {
        ...state,
        lastEditedTextStyle
      };
    }

    case Actions.DID_LOAD_TEMPLATES: {
      return {
        ...state,
        allTemplates: action.templates
      };
    }

    case Actions.THEME_CHANGED:
      return {
        ...state,
        detachColorTheme: false
      };

    case Actions.DETATCH_COLOR_THEME:
      return {
        ...state,
        detachColorTheme: true
      };

    case Actions.EDIT_SHAPE:
      return {
        ...state,
        editedShape: action.editedShape ?? null,
        proposedEditedShape: getShapePoints(action.editedShape.mask)
      };

    case Actions.CANCEL_EDIT_SHAPE:
    case Actions.CONFIRM_EDIT_SHAPE:
      return {
        ...state,
        editedShape: null,
        proposedEditedShape: null
      };

    // case Actions.SAVE_STATE_CHANGED: {
    //   if ((action.state as SaveState) !== SaveState.Unsaved) {
    //     return state;
    //   }
    //   return {
    //     ...state,
    //     hasUnrecordedChanges: true
    //   };
    // }

    case Actions.IMPORT_DESIGN:
      return {
        ...state,
        importDesign: true
      };

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/state/AppReducer.ts